import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MinhasSolicitacoes, SolicitacaoSuporteRequest, SolicitacaoSuporteResponse } from '../../../pages/models/suporte.dto';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';


@Injectable({
  providedIn: 'root'
})
export class SuporteService {
  private apiUrl = `${environment.apiSuporte}`; // base

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Envia uma nova solicitação de suporte
   */
  enviarSolicitacao(solicitacao: SolicitacaoSuporteRequest): Observable<SolicitacaoSuporteResponse> {
    const usuarioId = localStorage.getItem('usuarioId') || null;

    const buildPayload = async () => {
      const fotosBase64: string[] = [];
      if (solicitacao.foto) {
        const b = await this.fileToBase64(solicitacao.foto);
        fotosBase64.push(b);
      }
      if (solicitacao.fotosBase64 && solicitacao.fotosBase64.length) {
        fotosBase64.push(...solicitacao.fotosBase64);
      }

      return {
        usuarioId: usuarioId,
        vistoriaId: solicitacao.vistoriaId || null,
        titulo: solicitacao.titulo,
        numeroVistoria: solicitacao.numeroVistoria || null,
        descricao: solicitacao.descricao,
        fotosBase64: fotosBase64
      };
    };

    return from(buildPayload()).pipe(
      switchMap((body) => this.http.post<SolicitacaoSuporteResponse>(`${this.apiUrl}/api/tickets`, body, { headers: this.getHeaders() }))
    );
  }

  /**
   * Obtém todas as solicitações do usuário logado
   */
  obterMinhasSolicitacoes(page = 0, size = 20): Observable<MinhasSolicitacoes> {
    const usuarioId = localStorage.getItem('usuarioId') || '';
    const url = `${this.apiUrl}/api/tickets?usuarioId=${usuarioId}&page=${page}&size=${size}`;
    return this.http.get<MinhasSolicitacoes>(url, { headers: this.getHeaders() });
  }

  /**
   * Obtém tickets abertos (visão de superadmin)
   */
  obterTicketsAbertos(page = 0, size = 50): Observable<MinhasSolicitacoes> {
    const url = `${this.apiUrl}/api/tickets?status=ABERTO&page=${page}&size=${size}`;
    return this.http.get<MinhasSolicitacoes>(url, { headers: this.getHeaders() });
  }

  /**
   * Obtém detalhes de uma solicitação específica
   */
  obterSolicitacaoPorId(id: string): Observable<SolicitacaoSuporteResponse> {
    return this.http.get<SolicitacaoSuporteResponse>(`${this.apiUrl}/api/tickets/${id}`, { headers: this.getHeaders() });
  }

  /**
   * Cancela uma solicitação
   */
  cancelarSolicitacao(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/tickets/${id}`, { headers: this.getHeaders() });
  }

  /**
   * Atualiza apenas o status da solicitação (PATCH /api/tickets/{id}/status)
   */
  atualizarStatus(id: string, status: string): Observable<any> {
    const url = `${this.apiUrl}/api/tickets/${id}/status`;
    return this.http.patch(url, { status }, { headers: this.getHeaders() });
  }

  /**
   * Atualiza uma solicitação existente
   */
  atualizarSolicitacao(id: string, solicitacao: SolicitacaoSuporteRequest): Observable<SolicitacaoSuporteResponse> {
    const usuarioId = localStorage.getItem('usuarioId') || null;

    const buildPayload = async () => {
      const fotosBase64: string[] = [];
      if (solicitacao.foto) {
        const b = await this.fileToBase64(solicitacao.foto);
        fotosBase64.push(b);
      }
      if (solicitacao.fotosBase64 && solicitacao.fotosBase64.length) {
        fotosBase64.push(...solicitacao.fotosBase64);
      }

      return {
        usuarioId: usuarioId,
        vistoriaId: solicitacao.vistoriaId || null,
        titulo: solicitacao.titulo,
        numeroVistoria: solicitacao.numeroVistoria || null,
        descricao: solicitacao.descricao,
        fotosBase64: fotosBase64
      };
    };

    return from(buildPayload()).pipe(
      switchMap((body) => this.http.put<SolicitacaoSuporteResponse>(`${this.apiUrl}/api/tickets/${id}`, body, { headers: this.getHeaders() }))
    );
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken() || localStorage.getItem('token') || '';
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // remove mime prefix
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }
}
